<template>
	<LayoutScreen background="--background-base" max-width="500px">
		<!-- A plan that is not a commitment keeps the screen as it was before DEV-35727 (owner, 2026-10-22):
		     the Back header, everything centred, Submit pinned in the footer. -->
		<template v-if="classic" #header>
			<LayoutFlex padding="0 var(--size-20)" gap="0">
				<CancelHeader />
			</LayoutFlex>
		</template>

		<!-- Figma 7415:16355 (a commitment): no header — the title starts 24 from the top; everything
		     left-aligned, 28 apart, Submit in the page 28 under the list. -->
		<template #content>
			<LayoutFlex v-if="classic" padding="var(--size-24) var(--size-20)" gap="var(--size-28)" align-items="center">
				<Text font="--heading-h2" color="--text-dark-primary" text-align="center">
					If you continue cancelling your KOVA plan
				</Text>
				<Text font="--text-18-22-reg" color="--text-dark-primary" text-align="center">
					When your subscription ends on
					<strong>{{ endDateLabel }}</strong>
					, you’ll no longer be enrolled in KOVA.
				</Text>
				<Text font="--text-18-22-reg" color="--text-dark-primary" text-align="center">
					Which means you’ll lose access to:
				</Text>

				<LayoutFlex padding="0" gap="0" min-width="100%">
					<LayoutFlex
						v-for="(item, i) in LOSES_CLASSIC"
						:key="item"
						flex-direction="row"
						align-items="center"
						gap="var(--size-8)"
						padding="var(--size-16) 0"
						min-width="100%"
						:style="{
							borderBottom: i < LOSES_CLASSIC.length - 1 ? '1px solid var(--stroke-dark-low)' : undefined,
						}"
					>
						<Icon type="ds_kova_close" size="--size-16" color="#CB3152" />
						<Text font="--text-16-22-reg" color="--text-dark-primary">{{ item }}</Text>
					</LayoutFlex>
				</LayoutFlex>

				<LayoutFlex padding="0" gap="var(--size-12)" align-items="center">
					<Text font="--text-18-22-bold" color="--text-dark-primary" text-align="center">
						Do you still want to cancel your KOVA plan?
					</Text>
				</LayoutFlex>

				<!-- :commit-delay-ms="0": see the commitment's Select below. -->
				<Select type="single" :value="confirmIntent" gap="var(--size-8)" :commit-delay-ms="0" @ds:change="onChange">
					<template #options>
						<SelectItem value="yes_cancel">
							<Text font="--text-16-20-semi" color="--text-dark-primary">Yes, cancel</Text>
							<SelectIndicator mode="always" />
						</SelectItem>
						<SelectItem value="keep_my_plan">
							<Text font="--text-16-20-semi" color="--text-dark-primary">No, I’d like to keep my plan</Text>
							<SelectIndicator mode="always" />
						</SelectItem>
					</template>
				</Select>

				<div v-if="store.error" class="confirm__error" role="alert">
					<Text font="--text-14-20-reg" color="--text-accent-rose">{{ store.error }}</Text>
				</div>
			</LayoutFlex>

			<LayoutFlex v-else padding="var(--size-24) var(--size-20)" gap="var(--size-28)">
				<LayoutFlex padding="0" gap="var(--size-12)" align-items="flex-start">
					<Text font="--heading-h2" color="--text-dark-primary">If you continue cancelling your Kova plan</Text>
					<!-- The comma must touch the date, which prettier would split off. -->
					<!-- prettier-ignore -->
					<Text font="--text-16-22-reg" color="--text-dark-primary">
						When your subscription ends on <span class="confirm__date">{{ endDateLabel }}</span>, you’ll no longer
						be enrolled in Kova.
					</Text>
				</LayoutFlex>

				<LayoutFlex
					padding="var(--size-20)"
					gap="var(--size-12)"
					align-items="flex-start"
					:background="KOVA.errorBg"
					corner-radius="var(--size-24)"
				>
					<Text font="--text-16-22-semi" color="--text-dark-primary">You’ll lose access to:</Text>
					<LayoutFlex
						v-for="item in LOSES"
						:key="item"
						class="confirm__loss"
						flex-direction="row"
						align-items="center"
						gap="var(--size-8)"
						padding="0"
					>
						<RoseCross />
						<Text font="--text-16-22-reg" color="--text-dark-primary">{{ item }}</Text>
					</LayoutFlex>
				</LayoutFlex>

				<LayoutFlex padding="0" gap="var(--size-16)">
					<Text font="--text-18-22-bold" color="--text-dark-primary">Do you still want to cancel your Kova plan?</Text>
					<!-- :commit-delay-ms="0": DS ≥0.36 delays single-select commits 250 ms by default;
					     the selection gates the Submit CTA, so the commit must be instant. -->
					<Select type="single" :value="confirmIntent" gap="var(--size-8)" :commit-delay-ms="0" @ds:change="onChange">
						<template #options>
							<SelectItem value="yes_cancel">
								<Text font="--text-16-20-semi" color="--text-dark-primary">Yes, cancel</Text>
								<SelectIndicator mode="always" />
							</SelectItem>
							<SelectItem value="keep_my_plan">
								<Text font="--text-16-20-semi" color="--text-dark-primary">No, I’d like to keep my plan</Text>
								<SelectIndicator mode="always" />
							</SelectItem>
						</template>
					</Select>
				</LayoutFlex>

				<Button
					full-width
					:disabled="(!geo.isOneClickCancelRequired && !confirmIntent) || store.loading"
					:loading="store.loading"
					@ds:click="onSubmit"
				>
					<Text font="--text-16-18-semi">Submit</Text>
				</Button>
			</LayoutFlex>
		</template>

		<template v-if="classic" #footer>
			<LayoutFlex padding="0 var(--size-20) var(--size-24)" gap="0">
				<Button
					full-width
					:disabled="(!geo.isOneClickCancelRequired && !confirmIntent) || store.loading"
					:loading="store.loading"
					@ds:click="onSubmit"
				>
					<Text font="--text-16-18-semi">Submit</Text>
				</Button>
			</LayoutFlex>
		</template>
	</LayoutScreen>

	<!-- Commitment only (DEV-35727): the cancel call failed (500 / no connection), nothing was
	     cancelled. "Try again" re-runs it here. The classic flow keeps its inline error above. -->
	<Modal :visible="cancelFailed" fullscreen animation="none">
		<CancelErrorScreen :step="step.title" @retry="retrySubmit" />
	</Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
	Button,
	Icon,
	LayoutFlex,
	LayoutScreen,
	Modal,
	Select,
	SelectIndicator,
	SelectItem,
	Text,
} from '@simple-life-app/growth-ui-kova'
import CancelHeader from '@/components/CancelHeader.vue'
import RoseCross from '@/components/cancel/RoseCross.vue'
import CancelErrorScreen from '@/components/cancel/CancelErrorScreen.vue'
import { AlreadyCancelledError, cancellationTypeForScope, type CancellationScope } from '@/api/medBilling'
import { useCancelFlowStore } from '@/store/cancelFlow'
import { useGeoStore } from '@/store/geo'
import { useSubscriptionStore } from '@/store/subscription'
import { useSurveyStore } from '@/store/survey'
import { KOVA } from '@/designTokens'
import { STEP_TITLE, stateChangeOnCancel, useCancelStepLogging } from '@/analytics'

const router = useRouter()
const store = useSubscriptionStore()
const cancelFlow = useCancelFlowStore()
// Not a commitment (`supply_schedule.term_type` is neither `commitment` nor `upfront`): the old screen.
// A paid-in-full plan is billed on a `regular` phase, so the phase type can't tell.
const classic = computed(() => !cancelFlow.kind)
const survey = useSurveyStore()
const geo = useGeoStore()

const LOSES = ['1:1 clinical care from your provider', 'Ongoing GLP-1 medication treatment', 'All Kova Guides'] as const
const LOSES_CLASSIC = [
	'1:1 clinical care from your provider',
	'Ongoing GLP-1 medication treatment',
	'All KOVA Guides',
] as const

const confirmIntent = computed<string>(() => {
	const raw = survey.answers['cancel_confirmed']
	return typeof raw === 'string' ? raw : ''
})

// This screen fires the only cancel call in the flow, so it spends the `/cancel/scope` answer:
// "plan and prescription" → `hard`, anything else (incl. absent) → `soft`.
//
// `isRxUnderReview` is a second gate: `startFlow()` guarantees the answer belongs to this pass, but
// only for the `/status` entry points — not a Back into a stale `/cancel/confirm` or a deep link.
// It reads the last fetch, not live state (nothing refetches mid-flow), so it can be stale both
// ways; `soft` is the safe direction either way, and the silent downgrade is a known copy gap.
// Full reasoning — including why post-approval `hard` wouldn't save the prescription — in ADR 0014.
//
// A commitment plan under review takes the same rule (DEV-35727): its scripted way here is "Cancel my
// KOVA plan now" on `/cancel/scope` → `hard` ("renewals only" cancels `soft` on /cancel/likelihood).
// Off that path — browser Forward after switching to "renewals only", a deep link — the answer still
// decides, and none means `soft`, so the prescription is never cancelled unasked. Off review, a
// commitment gets here only in the last month of its term and cancels `soft` like everything else —
// a refill in review included: only the first prescription's review is the commitment's `review` stage.
const cancellationType = computed(() => {
	if (!(classic.value ? store.isRxUnderReview : cancelFlow.stage === 'review')) return 'soft'
	const raw = survey.answers['cancellation_scope']
	return cancellationTypeForScope(typeof raw === 'string' ? (raw as CancellationScope) : undefined)
})

// The soft-cancel access horizon, not the next charge: a commitment winds down to the end of its
// cycle, so `getAccessEndsAt` prefers `commitment_end_at` there (see the store). A `hard` cancel ends
// access now — billing sets `expired_at` to the cancel moment (`plainHorizon`) and quotes no date for it
// beforehand — so it reads today (DEV-36623).
// A commitment in its last month reaches here with `end_of_period` offered: its `access_until` is the
// backend's own date for this cancel (DEV-35727). Non-commitment plans keep the old source.
const endDateLabel = computed(() => {
	const endOfPeriod = classic.value ? undefined : cancelFlow.quotes.endOfPeriod?.access_until
	const iso = cancellationType.value === 'hard' ? new Date().toISOString() : (endOfPeriod ?? store.getAccessEndsAt)
	if (!iso) return '—'
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return iso
	if (classic.value) return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) // 4/16/2027
	return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) // 04/16/2027
})

const step = useCancelStepLogging(STEP_TITLE.DO_YOU_STILL_WANT_TO_CANCEL, {
	getResponses: () => (confirmIntent.value ? [confirmIntent.value] : []),
})

function onChange(next: string | string[]) {
	const value = Array.isArray(next) ? (next[0] ?? '') : next
	survey.setAnswer('cancel_confirmed', value)
	step.record(value ? [value] : [])
}

const cancelFailed = ref(false)

function retrySubmit() {
	cancelFailed.value = false
	onSubmit()
}

async function onSubmit() {
	if (confirmIntent.value === 'keep_my_plan') {
		// "Keep my plan" — user is leaving the cancel flow back to the dashboard.
		// Drop the partial survey so a future cancel attempt starts fresh.
		survey.reset()
		router.push('/status')
		return
	}
	// California: empty selection must still cancel. Anyone else: ignore the click
	// (button shouldn't even be enabled in that case, but guard defensively).
	const intentToCancel = confirmIntent.value === 'yes_cancel' || geo.isOneClickCancelRequired
	if (!intentToCancel) return
	// One cancel per submit: a double tap must not fire a second request while the first is in flight.
	if (store.loading) return
	try {
		// Capture the pre-cancel state before the store flips `cancelled` to true.
		const changed = stateChangeOnCancel(cancellationType.value === 'hard' ? 'hard' : 'soft')
		await store.cancel({ cancellationType: cancellationType.value })
		// Emit the state change only after the API accepts the cancellation.
		changed()
		// Only after the cancel succeeds — drop the survey so the receipt
		// path (`/cancel/done` → `/cancel/feedback`) doesn't carry stale state.
		// If `store.cancel` throws, we keep the answers so the user can retry.
		survey.reset()
		// Routes are eager (single bundle), so this navigation can't fail on a lazy-chunk
		// import — a plain push reliably lands on the receipt. (Earlier this needed a
		// `window.location.assign` fallback because the lazy `/cancel/done` import could reject
		// after fetching — see docs/decisions/0009-eager-routes.md.)
		router.push('/cancel/done')
	} catch (err) {
		// A `hard` over an earlier cancel ("renewals only" under review): billing refused it, nothing changed.
		// No receipt and no retry — /status shows the plan as it is.
		if (err instanceof AlreadyCancelledError) {
			router.replace('/status')
			return
		}
		// store.error is set by the store: the classic flow renders it above the button, a commitment
		// gets the "Something went wrong" screen.
		if (!classic.value) cancelFailed.value = true
	}
}
</script>

<style scoped>
.confirm__date {
	font-weight: var(--fw-semi);
}

/* "Ongoing GLP-1 medication treatment" is 278.3px at 16px Inter and has 278 beside its icon at 390:
   the frame keeps it on one line, so a row may run 4px into the card's right padding. */
.confirm__loss {
	margin-right: calc(-1 * var(--size-4));
}

.confirm__error {
	width: 100%;
}
</style>
